import React, { useState } from 'react';
import { 
  Modal, 
  Box, 
  Typography, 
  Button, 
  CircularProgress, 
  Alert,
  Divider,
  Stack
} from '@mui/material';
import { MoneyOff, Warning, ErrorOutline } from '@mui/icons-material';
import api from '../../../services/api';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 500,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
  maxWidth: '90vw'
};

const RefundModal = ({ open, onClose, campaign, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleRefund = async () => {
    if (!campaign) return;
    setLoading(true);
    setError(null);
    setSuccess(false);
    
    try {
      await api.post(`/v1/api/admin/campaigns/${campaign.id}/refund`);
      setSuccess(true);
      onSuccess();
      setTimeout(() => {
        onClose();
        setSuccess(false);
      }, 1500);
    } catch (err) {
      console.error('Refund error:', err);
      setError(err.response?.data?.message || 
              err.message || 
              'Failed to process refunds. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      setError(null);
      setSuccess(false);
    }
  };

  return (
    <Modal 
      open={open} 
      onClose={handleClose}
      aria-labelledby="refund-modal-title"
      aria-describedby="refund-modal-description"
    >
      <Box sx={style}>
        <Typography id="refund-modal-title" variant="h6" component="h2" gutterBottom>
          Refund Donations
        </Typography>
        <Divider sx={{ mb: 3 }} />
        
        <Typography variant="subtitle1" gutterBottom>
          Campaign: <strong>{campaign?.title}</strong>
        </Typography>
        
        <Alert 
          severity="warning" 
          icon={<Warning fontSize="inherit" />} 
          sx={{ mb: 3 }}
        >
          This action will refund all donations to this campaign. This cannot be undone.
        </Alert>
        
        <Stack spacing={1} sx={{ mb: 3 }}>
          <Typography>
            Total amount to refund: <strong>${campaign?.amountRaised?.toLocaleString()}</strong>
          </Typography>
          <Typography>
            Number of donors affected: <strong>{campaign?.donors}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Refunds will be processed using the original payment methods.
          </Typography>
        </Stack>

        {error && (
          <Alert 
            severity="error" 
            icon={<ErrorOutline fontSize="inherit" />}
            sx={{ mb: 3 }}
          >
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Refunds processed successfully!
          </Alert>
        )}

        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          gap: 2, 
          mt: 3 
        }}>
          <Button 
            onClick={handleClose}
            disabled={loading}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={loading ? <CircularProgress size={20} /> : <MoneyOff />}
            onClick={handleRefund}
            disabled={loading || success}
          >
            {loading ? 'Processing...' : 'Confirm Refund'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default RefundModal;